<template>
    <div>
        <card-header icon="fa-life-ring">
            <template #title>
                <span v-if="ticket.subject">{{ticket.subject}}</span>
                <span v-else>Loading...</span>
            </template>
        </card-header>

        <div class="viewing-banner" v-if="otherViewers.length">
            <i class="fas fa-eye mr-1"></i>
            <span v-for="(v, i) in otherViewers" :key="v.userId">{{ v.name }}<span v-if="i < otherViewers.length - 1">, </span></span>
            {{ ' ' + (otherViewers.length === 1 ? 'is' : 'are') }} also viewing this ticket
        </div>

        <card-toolbar v-if="ticket.id">
            <b-tooltip label="Reply">
                <button class="icon-only" @click="showReply = !showReply; showNote = false"><i class="far fa-reply"></i></button>
            </b-tooltip>
            <b-tooltip label="Internal Note">
                <button class="icon-only" @click="showNote = !showNote; showReply = false"><i class="far fa-sticky-note"></i></button>
            </b-tooltip>
            <b-tooltip label="Create Task">
                <button class="icon-only" @click="showTaskForm = !showTaskForm"><i class="far fa-tasks"></i></button>
            </b-tooltip>
            <b-tooltip label="Ticket Details">
                <button class="icon-only" @click="showDetails = !showDetails"><i class="far fa-sliders-h"></i></button>
            </b-tooltip>
            <template v-slot:right>
                <b-tooltip label="Put On Hold" v-if="ticket.status !== 'on_hold' && ticket.status !== 'closed'">
                    <button class="icon-only" @click="updateStatus('on_hold')"><i class="far fa-pause"></i></button>
                </b-tooltip>
                <b-tooltip label="Resume" v-if="ticket.status === 'on_hold'">
                    <button class="icon-only" @click="updateStatus('open')"><i class="far fa-play"></i></button>
                </b-tooltip>
                <b-tooltip label="Close Ticket" v-if="ticket.status !== 'closed'">
                    <button class="icon-only" @click="updateStatus('closed')"><i class="far fa-check"></i></button>
                </b-tooltip>
                <b-tooltip label="Reopen Ticket" v-if="ticket.status === 'closed'">
                    <button class="icon-only" @click="updateStatus('open')"><i class="far fa-envelope-open"></i></button>
                </b-tooltip>
            </template>
        </card-toolbar>

        <card-body toolbar v-if="ticket.id">
            <card-list v-if="ticket.user_id">
                <user-tile :card="card" :user="ticketUser" @click="$openCard('user', {userId: ticket.user_id}, card)"/>
                <separator-list-item/>
            </card-list>

            <div v-if="showDetails">
                <data-row title="Priority">
                    <select v-model="ticket.priority" @change="updateField('priority', ticket.priority)" class="inline-select">
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                    </select>
                </data-row>
                <data-row title="Assigned To">
                    <select v-model="ticket.assigned_to" @change="updateField('assigned_to', ticket.assigned_to)" class="inline-select">
                        <option :value="null">Unassigned</option>
                        <option v-for="pu in portalUsers" :key="pu.id" :value="pu.id">{{pu.first_name}} {{pu.last_name}}</option>
                    </select>
                </data-row>
                <data-row title="User">
                    <div v-if="ticket.user_id" class="selected-ticket-user">
                        <i class="fas fa-user mr-1"></i>{{ticket.user_first_name}} {{ticket.user_last_name}} ({{ticket.user_email}})
                        <button class="delete is-small ml-2" @click="clearTicketUser"></button>
                    </div>
                    <div v-else>
                        <b-input v-model="userSearch" @input="searchTicketUser" placeholder="Search users..." size="is-small"></b-input>
                        <div class="ticket-user-results" v-if="userResults.length">
                            <div v-for="u in userResults" :key="u.id" class="ticket-user-result" @click="selectTicketUser(u)">
                                <strong>{{u.first_name}} {{u.last_name}}</strong> - {{u.email}}
                            </div>
                        </div>
                    </div>
                </data-row>
                <separator-list-item/>
            </div>

            <!-- Messages thread -->
            <card-list>
                <subheader-list-item title="Conversation" icon="fa-comments" :badge="ticket.messages ? ticket.messages.length : 0"/>

                <!-- Reply form -->
                <div v-if="showReply" class="compose-box">
                    <div class="compose-header"><i class="far fa-reply mr-2"></i>Reply</div>
                    <div class="compose-from" v-if="ticket.channel_name">
                        <i class="far fa-fw mr-1" :class="ticket.channel_type === 'sms' ? 'fa-comment' : (ticket.channel_type === 'direct' ? 'fa-comments' : 'fa-envelope')"></i>
                        <span>{{ticket.channel_name}}</span>
                        <span v-if="ticket.domain_name" class="compose-domain-tag" :style="composeDomainStyle">{{ticket.domain_name}}</span>
                    </div>
                    <div class="compose-to" v-if="ticket.channel_type === 'sms'">To: {{ticket.phone_from}}</div>
                    <div class="compose-to" v-else-if="ticket.channel_type === 'direct'">To: Direct Message</div>
                    <div class="compose-to" v-else>To: {{ticket.email_from}}</div>
                    <b-select v-model="selectedCannedResponse" @input="applyCannedResponse" size="is-small" class="mb-2" v-if="cannedResponses.length">
                        <option :value="null">Insert canned response...</option>
                        <option v-for="cr in cannedResponses" :key="cr.id" :value="cr">{{cr.title}}</option>
                    </b-select>
                    <div v-if="ticket.channel_type === 'sms'">
                        <b-input type="textarea" v-model="replyText" rows="4" placeholder="Type your SMS reply..." class="mb-2"></b-input>
                        <div class="sms-char-count has-text-grey is-size-7">{{(replyText || '').length}} / 160 characters</div>
                    </div>
                    <div v-else>
                        <quill-editor v-model="replyHtml" ref="replyEditor" :options="editorOptions" @ready="setupPasteHandler($refs.replyEditor)" class="has-background-white content"></quill-editor>
                    </div>
                    <div v-if="ticket.channel_signature_html && ticket.channel_type === 'email'" class="compose-signature">
                        <div class="compose-signature-label"><i class="far fa-signature mr-1"></i>Signature</div>
                        <div class="compose-signature-preview" v-html="ticket.channel_signature_html"></div>
                        <hr class="compose-divider">
                    </div>
                    <div v-if="replyFiles.length && ticket.channel_type !== 'sms'" class="attached-files">
                        <div v-for="(file, i) in replyFiles" :key="i" class="attached-file">
                            <i class="far fa-paperclip mr-1"></i>{{file.name}} <span class="file-size">({{formatFileSize(file.size)}})</span>
                            <a @click="replyFiles.splice(i, 1)" class="remove-file"><i class="far fa-times"></i></a>
                        </div>
                    </div>
                    <div class="compose-actions">
                        <b-button type="is-primary" size="is-small" @click="sendReply" :loading="sending">Send Reply</b-button>
                        <b-button size="is-small" @click="$refs.replyFileInput.click()" v-if="ticket.channel_type !== 'sms'"><i class="far fa-paperclip"></i></b-button>
                        <input type="file" ref="replyFileInput" multiple @change="onFilesSelected" style="display: none;">
                        <b-button size="is-small" @click="showReply = false">Cancel</b-button>
                    </div>
                </div>

                <!-- Internal Note form -->
                <div v-if="showNote" class="compose-box note-box">
                    <div class="compose-header"><i class="far fa-sticky-note mr-2"></i>Internal Note</div>
                    <quill-editor v-model="noteHtml" ref="noteEditor" :options="editorOptions" @ready="setupPasteHandler($refs.noteEditor)" class="has-background-white content"></quill-editor>
                    <div class="compose-actions">
                        <b-button type="is-warning" size="is-small" @click="sendNote" :loading="sending">Save Note</b-button>
                        <b-button size="is-small" @click="showNote = false">Cancel</b-button>
                    </div>
                </div>

                <!-- Task form -->
                <div v-if="showTaskForm" class="compose-box">
                    <div class="compose-header"><i class="far fa-tasks mr-2"></i>Create Task</div>
                    <b-input v-model="taskForm.title" placeholder="Task title" size="is-small" class="mb-2"></b-input>
                    <b-input type="textarea" v-model="taskForm.description" placeholder="Description (optional)" rows="2" size="is-small" class="mb-2"></b-input>
                    <b-select v-model="taskForm.assigned_to" size="is-small" class="mb-2">
                        <option :value="null">Assign to...</option>
                        <option v-for="pu in portalUsers" :key="pu.id" :value="pu.id">{{pu.first_name}} {{pu.last_name}}</option>
                    </b-select>
                    <b-input v-model="taskForm.due_date" type="date" size="is-small" class="mb-2"></b-input>
                    <div class="compose-actions">
                        <b-button type="is-primary" size="is-small" @click="createTask">Create Task</b-button>
                        <b-button size="is-small" @click="showTaskForm = false">Cancel</b-button>
                    </div>
                </div>

                <div v-for="msg in sortedMessages" :key="msg.id" class="message-item" :class="{'inbound': msg.is_inbound, 'outbound': !msg.is_inbound, 'internal-note': msg.is_internal_note}">
                    <div class="message-header">
                        <div class="message-sender">
                            <i class="fas fa-fw mr-1 msg-icon" :class="msg.is_internal_note ? 'fa-sticky-note icon-note' : (msg.is_inbound ? 'fa-arrow-down icon-inbound' : 'fa-arrow-up icon-outbound')"></i>
                            <strong>{{msg.is_inbound ? msg.sender : (msg.portal_user_first_name ? msg.portal_user_first_name + ' ' + msg.portal_user_last_name : msg.sender)}}</strong>
                            <span class="note-badge" v-if="msg.is_internal_note">Internal Note</span>
                            <b-tooltip label="Forward" v-if="!msg.is_internal_note && ticket.channel_type === 'email'">
                                <a class="forward-btn" @click="openForward(msg)"><i class="far fa-share"></i></a>
                            </b-tooltip>
                        </div>
                        <div class="message-date">{{msg.created_at | moment('MMM Do, YYYY h:mm A')}} <span class="has-text-grey-light">({{relativeTime(msg.created_at)}})</span></div>
                    </div>
                    <iframe v-if="msg.body_html" class="message-iframe" :srcdoc="msg.body_html" sandbox="allow-same-origin" frameborder="0" @load="resizeIframe($event)"></iframe>
                    <div class="message-body" v-else>{{msg.body_text}}</div>
                    <div class="message-attachments" v-if="msg.attachments && msg.attachments.length">
                        <a v-for="att in msg.attachments" :key="att.id" :href="spacesUrl + '/' + att.storage_key" target="_blank" class="attachment">
                            <i class="far fa-paperclip mr-1"></i>{{att.filename}} <span class="att-size">({{formatSize(att.size)}})</span>
                        </a>
                    </div>
                    <!-- Email tracking status -->
                    <div class="tracking-row" v-if="!msg.is_inbound && !msg.is_internal_note && msg.mailgun_id">
                        <span v-if="!msg.events || !msg.events.length" class="tracking-badge tracking-pending">
                            <i class="far fa-clock"></i> Pending
                        </span>
                        <template v-else>
                            <b-tooltip v-for="evt in uniqueEvents(msg.events)" :key="evt.id" :label="eventTooltip(evt)" position="is-top" multilined>
                                <span class="tracking-badge" :class="'tracking-' + evt.event">
                                    <i :class="eventIcon(evt.event)"></i> {{eventLabel(evt.event)}}
                                </span>
                            </b-tooltip>
                        </template>
                        <b-tooltip label="Refresh tracking" position="is-top">
                            <a class="tracking-refresh" @click="refreshTracking(msg)" :class="{'is-loading': msg._refreshing}">
                                <i class="far fa-sync-alt" :class="{'fa-spin': msg._refreshing}"></i>
                            </a>
                        </b-tooltip>
                        <a v-if="msg.events && msg.events.length" class="tracking-expand" @click="$set(msg, '_expanded', !msg._expanded)">
                            <i class="far" :class="msg._expanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        </a>
                    </div>
                    <!-- Expanded event activity log -->
                    <div class="tracking-activity" v-if="msg._expanded && msg.events && msg.events.length">
                        <div class="tracking-activity-item" v-for="evt in msg.events" :key="evt.id">
                            <i :class="eventIcon(evt.event)" class="tracking-activity-icon" :style="{color: eventColor(evt.event)}"></i>
                            <div class="tracking-activity-body">
                                <div class="tracking-activity-title">
                                    <strong>{{eventLabel(evt.event)}}</strong>
                                    <span v-if="evt.recipient" class="has-text-grey"> &mdash; {{evt.recipient}}</span>
                                </div>
                                <div class="tracking-activity-meta">
                                    {{evt.timestamp | moment('MMM Do, YYYY h:mm:ss A')}} <span class="has-text-grey-light">({{relativeTime(evt.timestamp)}})</span>
                                </div>
                                <div class="tracking-activity-detail" v-if="evt.reason">{{evt.reason}}</div>
                                <div class="tracking-activity-detail" v-if="evt.severity">Severity: {{evt.severity}}</div>
                                <div class="tracking-activity-detail" v-if="evt.ip">IP: {{evt.ip}} <span v-if="evt.country">({{evt.country}})</span></div>
                                <div class="tracking-activity-detail" v-if="evt.user_agent">{{evt.user_agent}}</div>
                                <div class="tracking-activity-detail" v-if="evt.url">URL: <a :href="evt.url" target="_blank">{{evt.url}}</a></div>
                            </div>
                        </div>
                    </div>
                    <!-- Forward compose box -->
                    <div v-if="forwardingMessageId === msg.id" class="compose-box forward-box">
                        <div class="compose-header"><i class="far fa-share mr-2"></i>Forward</div>
                        <b-input v-model="forwardTo" placeholder="Recipient email address" size="is-small" type="email" class="mb-2"></b-input>
                        <quill-editor v-model="forwardHtml" :options="editorOptions" class="has-background-white content"></quill-editor>
                        <div class="compose-actions">
                            <b-button type="is-primary" size="is-small" @click="sendForward(msg)" :loading="sending">Send Forward</b-button>
                            <b-button size="is-small" @click="forwardingMessageId = null">Cancel</b-button>
                        </div>
                    </div>
                </div>

                <container-list-item padded v-if="!ticket.messages || ticket.messages.length === 0" class="has-text-grey is-size-7">
                    No messages yet.
                </container-list-item>
            </card-list>

            <separator-list-item v-if="ticket.tasks && ticket.tasks.length"/>

            <!-- Tasks -->
            <card-list v-if="ticket.tasks && ticket.tasks.length">
                <subheader-list-item title="Tasks" icon="fa-tasks" :badge="ticket.tasks.length"/>
                <card-list-item v-for="task in ticket.tasks" :key="task.id">
                    <template v-slot:icon>
                        <i class="fa-fw" :class="taskStatusIcon(task.status)"></i>
                    </template>
                    <template v-slot:content>
                        <div class="tile-title">{{task.title}}</div>
                        <div class="meta" v-if="task.assigned_first_name"><i class="fas fa-user fa-fw mr-1"></i>{{task.assigned_first_name}} {{task.assigned_last_name}}</div>
                        <div class="meta" v-if="task.due_date"><i class="fas fa-calendar fa-fw mr-1"></i>{{task.due_date | moment('MMM Do, YYYY')}}</div>
                    </template>
                    <template v-slot:status>
                        <span class="task-status" :class="'task-' + task.status">{{task.status}}</span>
                    </template>
                </card-list-item>
            </card-list>
        </card-body>

    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardList from "@/TIER/components/CardList";
    import CardToolbar from "@/TIER/components/CardToolbar";
    import CardListItem from "@/TIER/components/CardListItem";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import SeparatorListItem from "@/TIER/components/SeparatorListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import DataRow from "@/TIER/components/DataRow";

    import UserTile from "../../components/tiles/UserTile";
    import {client as http} from '@/http_client';
    import moment from 'moment';
    import {getViewers} from '@/components/supportViewingTracker';

    export default {
        components: {ContainerListItem, SeparatorListItem, CardHeader, CardBody, CardList, CardToolbar, CardListItem, SubheaderListItem, DataRow, UserTile},
        props: ['card', 'child', 'props'],
        data() {
            return {
                ticket: {},
                portalUsers: [],
                cannedResponses: [],
                showReply: false,
                showNote: false,
                showTaskForm: false,
                showDetails: false,
                replyHtml: '',
                replyText: '',
                noteHtml: '',
                selectedCannedResponse: null,
                replyFiles: [],
                forwardingMessageId: null,
                forwardTo: '',
                forwardHtml: '',
                sending: false,
                editorOptions: {
                    modules: {
                        toolbar: [
                            ['bold', 'italic', 'underline'],
                            [{'color': []}, {'background': []}],
                            [{'list': 'ordered'}, {'list': 'bullet'}],
                            ['link', 'image'],
                            ['clean']
                        ],
                        keyboard: {
                            bindings: {
                                linebreak: {
                                    key: 13,
                                    shiftKey: true,
                                    handler: function(range) {
                                        this.quill.insertEmbed(range.index, 'break', true, 'user');
                                        this.quill.setSelection(range.index + 1, 'silent');
                                        return false;
                                    }
                                }
                            }
                        }
                    },
                    placeholder: ''
                },
                taskForm: {
                    title: '',
                    description: '',
                    assigned_to: null,
                    due_date: null
                },
                spacesUrl: 'https://my247ed.sfo3.cdn.digitaloceanspaces.com',
                userSearch: '',
                userResults: [],
                searchTimeout: null,
                viewingInterval: null
            }
        },
        computed: {
            sortedMessages() {
                if (!this.ticket.messages) return [];
                return [...this.ticket.messages].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            },
            ticketUser() {
                return {
                    id: this.ticket.user_id,
                    first_name: this.ticket.user_first_name,
                    last_name: this.ticket.user_last_name,
                    email: this.ticket.user_email,
                    email_2: this.ticket.user_email_2
                };
            },
            otherViewers() {
                if (!this.ticket.id) return [];
                let me = this.$store.getters.currentUser;
                return getViewers(this.ticket.id, me ? me.id : null);
            },
            composeDomainStyle() {
                if (!this.ticket.domain_color) return {};
                return {
                    background: this.ticket.domain_color,
                    color: '#fff'
                };
            }
        },
        methods: {
            reload() {
                this.loadTicket(true);
            },
            loadTicket(forced) {
                let ticketId = this.props.ticketId;
                if (!ticketId) return;

                http.get('support/tickets/' + ticketId, {force: forced}).then(response => {
                    this.ticket = response.data;
                    this.$emit('loading', false);
                    this.$emit('title', this.ticket.subject || 'Ticket #' + this.ticket.id);
                    this.loadCannedResponses();
                }).catch(err => {
                    console.log(err);
                    this.$emit('loading', false);
                });
            },
            loadPortalUsers() {
                http.get('portal-users').then(response => {
                    this.portalUsers = response.data || [];
                }).catch(() => {});
            },
            loadCannedResponses() {
                let params = [];
                if (this.ticket.domain_id) params.push('domain_id=' + this.ticket.domain_id);
                else if (this.ticket.channel_id) params.push('channel_id=' + this.ticket.channel_id);
                if (this.ticket.channel_type) params.push('channel_type=' + this.ticket.channel_type);
                let url = 'support/canned-responses' + (params.length ? '?' + params.join('&') : '');
                http.get(url).then(response => {
                    this.cannedResponses = response.data || [];
                }).catch(() => {});
            },
            updateField(field, value) {
                let update = {};
                update[field] = value;
                http.put('support/tickets/' + this.ticket.id, update).then(() => {}).catch(err => console.log(err));
            },
            updateStatus(status) {
                this.updateField('status', status);
                if (status === 'on_hold' || status === 'closed') {
                    this.$closeCard(this.card);
                } else {
                    this.ticket.status = status;
                }
            },
            applyCannedResponse() {
                if (this.selectedCannedResponse) {
                    this.replyHtml = this.selectedCannedResponse.body_html || this.selectedCannedResponse.body_text || '';
                    this.selectedCannedResponse = null;
                }
            },
            sendReply() {
                let isSms = this.ticket.channel_type === 'sms';
                let bodyHtml = isSms ? this.replyText : this.replyHtml;
                let bodyText = isSms ? this.replyText : (this.replyHtml || '').replace(/<[^>]+>/g, '').trim();

                if (!bodyText || !bodyText.trim()) return;
                if (!isSms && this.replyHtml === '<p><br></p>') return;

                this.sending = true;
                let formData = new FormData();
                formData.append('body_text', bodyText);
                formData.append('body_html', bodyHtml);
                if (!isSms) {
                    for (let file of this.replyFiles) {
                        formData.append('attachments', file, file.name);
                    }
                }
                http.post('support/tickets/' + this.ticket.id + '/messages', formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                }).then(() => {
                    this.replyHtml = '';
                    this.replyText = '';
                    this.replyFiles = [];
                    this.showReply = false;
                    this.sending = false;
                    this.loadTicket(true);
                }).catch(err => {
                    console.log(err);
                    this.sending = false;
                });
            },
            onFilesSelected(e) {
                let files = e.target.files;
                if (files) {
                    for (let f of files) this.replyFiles.push(f);
                }
                e.target.value = '';
            },
            formatFileSize(bytes) {
                if (bytes < 1024) return bytes + ' B';
                if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
            },
            sendNote() {
                if (!this.noteHtml || !this.noteHtml.trim() || this.noteHtml === '<p><br></p>') return;
                this.sending = true;
                let plainText = this.noteHtml.replace(/<[^>]+>/g, '').trim();
                http.post('support/tickets/' + this.ticket.id + '/notes', {
                    body_text: plainText,
                    body_html: this.noteHtml
                }).then(() => {
                    this.noteHtml = '';
                    this.showNote = false;
                    this.sending = false;
                    this.loadTicket(true);
                }).catch(err => {
                    console.log(err);
                    this.sending = false;
                });
            },
            openForward(msg) {
                if (this.forwardingMessageId === msg.id) {
                    this.forwardingMessageId = null;
                    return;
                }
                this.forwardTo = '';
                let originalBody = msg.body_html || msg.body_text || '';
                this.forwardHtml = '<p><br></p>' +
                    '<div style="margin-top:16px; padding-top:12px; border-top:1px solid #ccc; color:#555;">' +
                    '<p style="margin:0 0 8px 0; font-size:12px;"><strong>---------- Forwarded message ----------</strong><br>' +
                    'From: ' + (msg.sender || 'Unknown') + '<br>' +
                    'Date: ' + this.$options.filters.moment(msg.created_at, 'MMM Do, YYYY h:mm A') + '<br>' +
                    'Subject: ' + (this.ticket.subject || '') + '</p>' +
                    '<div>' + originalBody + '</div></div>';
                this.forwardingMessageId = msg.id;
            },
            sendForward(msg) {
                if (!this.forwardTo || !this.forwardTo.trim()) return;
                this.sending = true;
                let plainText = (this.forwardHtml || '').replace(/<[^>]+>/g, '').trim();
                http.post('support/tickets/' + this.ticket.id + '/messages/' + msg.id + '/forward', {
                    to: this.forwardTo.trim(),
                    body_html: this.forwardHtml,
                    body_text: plainText
                }).then(() => {
                    this.forwardingMessageId = null;
                    this.forwardTo = '';
                    this.forwardHtml = '';
                    this.sending = false;
                    this.loadTicket(true);
                }).catch(err => {
                    console.log(err);
                    this.sending = false;
                });
            },
            createTask() {
                if (!this.taskForm.title.trim()) return;
                http.post('support/tasks', {
                    ...this.taskForm,
                    ticket_id: this.ticket.id
                }).then(() => {
                    this.taskForm = {title: '', description: '', assigned_to: null, due_date: null};
                    this.showTaskForm = false;
                    this.loadTicket(true);
                }).catch(err => console.log(err));
            },
            taskStatusIcon(status) {
                switch (status) {
                    case 'pending': return 'far fa-circle text-warning';
                    case 'in_progress': return 'fas fa-spinner text-primary';
                    case 'completed': return 'fas fa-check-circle text-success';
                    case 'cancelled': return 'fas fa-times-circle text-danger';
                    default: return 'far fa-circle';
                }
            },
            setupPasteHandler(editorRef) {
                if (!editorRef || !editorRef.quill) return;
                let quill = editorRef.quill;
                quill.root.addEventListener('paste', (e) => {
                    let items = (e.clipboardData || e.originalEvent.clipboardData).items;
                    for (let item of items) {
                        if (item.type.indexOf('image') !== -1) {
                            e.preventDefault();
                            e.stopPropagation();
                            let file = item.getAsFile();
                            this.uploadAndInsertImage(quill, file);
                            return;
                        }
                    }
                });
            },
            uploadAndInsertImage(quill, file) {
                let formData = new FormData();
                formData.append('image', file, file.name || 'paste.png');
                http.post('support/upload-image', formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                }).then(response => {
                    let range = quill.getSelection(true);
                    quill.insertEmbed(range.index, 'image', response.data.url);
                    let img = quill.root.querySelector(`img[src="${response.data.url}"]`);
                    if (img) img.setAttribute('style', 'max-width: 100%; height: auto;');
                    quill.setSelection(range.index + 1);
                }).catch(err => {
                    console.log('Image upload failed', err);
                });
            },
            resizeIframe(e) {
                let iframe = e.target;
                try {
                    let doc = iframe.contentDocument || iframe.contentWindow.document;
                    let style = doc.createElement('style');
                    style.textContent = 'html, body { margin: 0; padding: 0; overflow: hidden; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; font-size: 0.85rem; line-height: 1.5; color: #333; word-break: break-word; } img { max-width: 100%; height: auto; }';
                    doc.head.appendChild(style);
                    // Wait a tick for styles to apply before measuring
                    setTimeout(() => {
                        iframe.style.height = doc.body.scrollHeight + 'px';
                    }, 0);
                } catch (err) {}
            },
            relativeTime(date) {
                return moment(date).fromNow();
            },
            formatSize(bytes) {
                if (!bytes) return '';
                if (bytes < 1024) return bytes + ' B';
                if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
                return (bytes / 1048576).toFixed(1) + ' MB';
            },
            eventIcon(event) {
                switch (event) {
                    case 'delivered': return 'fas fa-check';
                    case 'opened': return 'fas fa-eye';
                    case 'clicked': return 'fas fa-mouse-pointer';
                    case 'bounced': return 'fas fa-exclamation-triangle';
                    case 'failed': return 'fas fa-times-circle';
                    case 'complained': return 'fas fa-flag';
                    default: return 'far fa-circle';
                }
            },
            eventColor(event) {
                switch (event) {
                    case 'delivered': return '#2e7d32';
                    case 'opened': return '#1565c0';
                    case 'clicked': return '#5e35b1';
                    case 'bounced': return '#e65100';
                    case 'failed': return '#c62828';
                    case 'complained': return '#ad1457';
                    default: return '#999';
                }
            },
            eventLabel(event) {
                return event.charAt(0).toUpperCase() + event.slice(1);
            },
            eventTooltip(evt) {
                let parts = [moment(evt.timestamp).format('MMM Do, YYYY h:mm A')];
                if (evt.recipient) parts.push('To: ' + evt.recipient);
                if (evt.severity) parts.push('Severity: ' + evt.severity);
                if (evt.reason) parts.push('Reason: ' + evt.reason);
                if (evt.ip) parts.push('IP: ' + evt.ip);
                if (evt.country) parts.push('Country: ' + evt.country);
                if (evt.user_agent) parts.push('UA: ' + evt.user_agent.substring(0, 80));
                if (evt.url) parts.push('URL: ' + evt.url.substring(0, 80));
                return parts.join('\n');
            },
            uniqueEvents(events) {
                // Show one badge per event type, using the latest occurrence
                let map = {};
                for (let evt of events) {
                    if (!map[evt.event] || new Date(evt.timestamp) > new Date(map[evt.event].timestamp)) {
                        map[evt.event] = evt;
                    }
                }
                let order = ['delivered', 'opened', 'clicked', 'bounced', 'failed', 'complained'];
                return order.filter(e => map[e]).map(e => map[e]);
            },
            searchTicketUser() {
                if (this.searchTimeout) clearTimeout(this.searchTimeout);
                if (!this.userSearch || this.userSearch.length < 2) {
                    this.userResults = [];
                    return;
                }
                this.searchTimeout = setTimeout(() => {
                    http.get('users/search/' + encodeURIComponent(this.userSearch) + '/0/10').then(response => {
                        this.userResults = response.data.users || response.data || [];
                    }).catch(() => {
                        this.userResults = [];
                    });
                }, 300);
            },
            selectTicketUser(user) {
                this.userSearch = '';
                this.userResults = [];
                this.ticket.user_id = user.id;
                this.ticket.user_first_name = user.first_name;
                this.ticket.user_last_name = user.last_name;
                this.ticket.user_email = user.email;
                this.updateField('user_id', user.id);
            },
            clearTicketUser() {
                this.ticket.user_id = null;
                this.ticket.user_first_name = null;
                this.ticket.user_last_name = null;
                this.ticket.user_email = null;
                this.updateField('user_id', null);
            },
            broadcastViewing() {
                let ticketId = this.props.ticketId;
                if (!ticketId) return;
                let user = this.$store.getters.currentUser;
                if (!user) return;
                try {
                    this.$socket.sendObj({
                        type: 'support_viewing',
                        action: 'viewing',
                        ticketId: parseInt(ticketId),
                        userId: user.id,
                        userName: (user.firstName || '') + ' ' + (user.lastName || '')
                    });
                } catch (e) {}
            },
            broadcastStopViewing() {
                let ticketId = this.props.ticketId;
                if (!ticketId) return;
                let user = this.$store.getters.currentUser;
                if (!user) return;
                try {
                    this.$socket.sendObj({
                        type: 'support_viewing',
                        action: 'stop',
                        ticketId: parseInt(ticketId),
                        userId: user.id,
                        userName: (user.firstName || '') + ' ' + (user.lastName || '')
                    });
                } catch (e) {}
            },
            refreshTracking(msg) {
                this.$set(msg, '_refreshing', true);
                http.post('support/messages/' + msg.id + '/events/refresh').then(response => {
                    this.$set(msg, 'events', response.data || []);
                    this.$set(msg, '_refreshing', false);
                }).catch(err => {
                    console.log(err);
                    this.$set(msg, '_refreshing', false);
                });
            }
        },
        sockets: {
            onmessage(event) {
                try {
                    let data = (typeof event.data === 'string') ? JSON.parse(event.data) : event.data;
                    if (data.type !== 'support') return;
                    let currentId = this.props && this.props.ticketId;
                    if (currentId == null) return;
                    let d = data.data || {};
                    let incomingId = (d.ticketId != null) ? d.ticketId : d.id;
                    if (incomingId != null && Number(incomingId) === Number(currentId)) {
                        this.loadTicket(true);
                    }
                } catch (e) {}
            }
        },
        watch: {
            'props.ticketId': function() {
                this.loadTicket();
            }
        },
        mounted() {
            this.loadTicket();
            this.loadPortalUsers();
            this.broadcastViewing();
            this.viewingInterval = setInterval(() => this.broadcastViewing(), 60000);
        },
        beforeDestroy() {
            if (this.viewingInterval) clearInterval(this.viewingInterval);
            this.broadcastStopViewing();
        }
    }
</script>

<style lang="scss" scoped>
    .viewing-banner {
        display: flex;
        align-items: center;
        padding: 6px 12px;
        background: #fff3cd;
        color: #856404;
        font-size: 0.8rem;
        font-weight: 600;
        border-bottom: 1px solid #ffc107;
    }

    .inline-select {
        border: 1px solid #ddd;
        border-radius: 4px;
        padding: 2px 6px;
        font-size: 0.85rem;
        background: #fff;
    }

    .compose-box {
        margin: 0.75rem;
        padding: 0.75rem;
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        background: #fafafa;
    }
    .note-box {
        border-color: #ffc107;
        background: #fffdf0;
    }
    .forward-box {
        border-color: #7c4dff;
        background: #f8f5ff;
    }
    .forward-btn {
        margin-left: 0.5rem;
        color: #999;
        cursor: pointer;
        font-size: 0.8rem;
    }
    .forward-btn:hover {
        color: #7c4dff;
    }
    .compose-header {
        font-weight: 600;
        margin-bottom: 0.5rem;
        font-size: 0.9rem;
    }
    .compose-from {
        font-size: 0.8rem;
        color: #333;
        margin-bottom: 0.25rem;
        display: flex;
        align-items: center;
        gap: 0.25rem;
    }
    .compose-domain-tag {
        display: inline-block;
        font-size: 0.7rem;
        font-weight: 600;
        padding: 1px 6px;
        border-radius: 3px;
        background: #e8eef4;
        color: #28547F;
        margin-left: 0.25rem;
    }
    .compose-to {
        font-size: 0.8rem;
        color: #666;
        margin-bottom: 0.5rem;
    }
    .compose-signature {
        margin-top: 0.5rem;
        border-top: 1px dashed #ddd;
        padding-top: 0.5rem;
    }
    .compose-signature-label {
        font-size: 0.7rem;
        color: #999;
        text-transform: uppercase;
        font-weight: 600;
        margin-bottom: 0.25rem;
    }
    .compose-signature-preview {
        font-size: 0.8rem;
        color: #666;
        padding: 0.25rem 0;
        max-height: 120px;
        overflow-y: auto;
    }
    .compose-divider {
        border: none;
        border-top: 1px solid #e0e0e0;
        margin: 0.5rem 0 0 0;
    }
    .attached-files {
        margin-top: 0.5rem;
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem;
    }
    .attached-file {
        display: inline-flex;
        align-items: center;
        background: #f5f5f5;
        border-radius: 4px;
        padding: 0.2rem 0.5rem;
        font-size: 0.8rem;
    }
    .attached-file .file-size { color: #999; margin-left: 0.25rem; }
    .attached-file .remove-file { margin-left: 0.4rem; color: #ff3860; cursor: pointer; }
    .compose-actions {
        margin-top: 0.5rem;
        display: flex;
        gap: 0.5rem;
    }

    .message-item {
        padding: 0.75rem 1rem;
        border-bottom: 1px solid #f0f0f0;
    }
    .message-item.inbound {
        border-left: 3px solid #3273dc;
    }
    .message-item.outbound {
        border-left: 3px solid #23d160;
    }
    .message-item.internal-note {
        background: #fffdf0;
        border-left: 3px solid #ffc107;
    }
    .message-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.5rem;
        padding: 0.4rem 0.6rem;
        border-radius: 4px;
    }
    .inbound .message-header {
        background: #eef4fc;
    }
    .outbound .message-header {
        background: #eefbf3;
    }
    .internal-note .message-header {
        background: #fef9e7;
    }
    .message-iframe {
        width: 100%;
        min-height: 40px;
        border: none;
        overflow: hidden;
    }
    .message-sender {
        font-size: 0.85rem;
    }
    .icon-inbound {
        color: #2958a3;
    }
    .icon-outbound {
        color: #1a9a4a;
    }
    .icon-note {
        color: #d4a017;
    }
    .message-date {
        font-size: 0.75rem;
        color: #666;
    }
    .message-body {
        font-size: 0.85rem;
        line-height: 1.5;
        word-break: break-word;
    }
    .note-badge {
        font-size: 0.7rem;
        background: #ffc107;
        color: #333;
        padding: 1px 6px;
        border-radius: 3px;
        margin-left: 0.5rem;
        font-weight: 600;
    }
    .message-attachments {
        margin-top: 0.5rem;
    }
    .attachment {
        display: inline-block;
        font-size: 0.8rem;
        padding: 2px 8px;
        background: #f5f5f5;
        border: 1px solid #e0e0e0;
        border-radius: 3px;
        margin-right: 0.5rem;
        color: #3273dc;
    }
    .att-size {
        color: #999;
    }

    .tile-title {
        font-size: 0.85rem;
        font-weight: 600;
    }
    .task-status {
        font-size: 0.7rem;
        text-transform: uppercase;
        font-weight: 600;
    }
    .task-pending { color: #ffc107; }
    .task-in_progress { color: #3273dc; }
    .task-completed { color: #23d160; }
    .task-cancelled { color: #dc3545; }

    .tracking-row {
        margin-top: 0.4rem;
        display: flex;
        align-items: center;
        gap: 0.35rem;
        flex-wrap: wrap;
    }
    .tracking-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        font-size: 0.7rem;
        padding: 1px 7px;
        border-radius: 3px;
        font-weight: 600;
        cursor: default;
    }
    .tracking-pending { background: #f5f5f5; color: #999; }
    .tracking-delivered { background: #e8f5e9; color: #2e7d32; }
    .tracking-opened { background: #e3f2fd; color: #1565c0; }
    .tracking-clicked { background: #ede7f6; color: #5e35b1; }
    .tracking-bounced { background: #fff3e0; color: #e65100; }
    .tracking-failed { background: #ffebee; color: #c62828; }
    .tracking-complained { background: #fce4ec; color: #ad1457; }
    .tracking-refresh {
        display: inline-flex;
        align-items: center;
        font-size: 0.7rem;
        color: #999;
        cursor: pointer;
        margin-left: 0.25rem;
    }
    .tracking-refresh:hover { color: #3273dc; }
    .tracking-expand {
        display: inline-flex;
        align-items: center;
        font-size: 0.7rem;
        color: #999;
        cursor: pointer;
        margin-left: 0.15rem;
    }
    .tracking-expand:hover { color: #3273dc; }

    .tracking-activity {
        margin-top: 0.5rem;
        border-left: 2px solid #e0e0e0;
        padding-left: 0.75rem;
    }
    .tracking-activity-item {
        display: flex;
        gap: 0.5rem;
        padding: 0.35rem 0;
        border-bottom: 1px solid #f5f5f5;
    }
    .tracking-activity-item:last-child {
        border-bottom: none;
    }
    .tracking-activity-icon {
        flex-shrink: 0;
        margin-top: 2px;
        font-size: 0.75rem;
    }
    .tracking-activity-body {
        flex: 1;
        min-width: 0;
    }
    .tracking-activity-title {
        font-size: 0.8rem;
        line-height: 1.3;
    }
    .tracking-activity-meta {
        font-size: 0.7rem;
        color: #888;
    }
    .tracking-activity-detail {
        font-size: 0.7rem;
        color: #666;
        word-break: break-all;
    }
    .tracking-activity-detail a {
        color: #3273dc;
    }

    .selected-ticket-user {
        display: inline-flex;
        align-items: center;
        background: #e8f4fd;
        padding: 4px 10px;
        border-radius: 4px;
        font-size: 0.85rem;
    }
    .ticket-user-results {
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        max-height: 150px;
        overflow-y: auto;
        margin-top: 4px;
    }
    .ticket-user-result {
        padding: 6px 10px;
        font-size: 0.85rem;
        cursor: pointer;
        &:hover {
            background: #f5f5f5;
        }
    }
</style>
